// Deep-link return across the magic-link round trip.
//
// Supabase's redirect allowlist strips URL fragments, so an auth email can only
// ever bring the user back to the bare origin. Before this, main.ts always sent
// a freshly signed-in user to #/edit — which is wrong for anyone who started on
// #/claim (they land on an empty profile they do not own yet) or on #/apply.
//
// So we stash the route they started on before sending the OTP and read it back
// on SIGNED_IN. localStorage, not sessionStorage: the link is frequently opened
// from a mail app in a different tab, and sessionStorage does not survive that.
//
// Only same-document hash routes are ever stored or returned — an absolute URL
// or protocol-relative path from a tampered value would turn this into an open
// redirect on the back of an auth callback.

const KEY = 'curate.onboarding.returnTo';

/** True for '#/claim?code=ABC' and friends; false for anything that leaves the app. */
function isSafeHashRoute(value: string): boolean {
  return /^#\/[A-Za-z0-9\-/?=&_.%]*$/.test(value);
}

export function rememberReturnTo(hashRoute: string): void {
  if (!isSafeHashRoute(hashRoute)) return;
  try {
    window.localStorage.setItem(KEY, hashRoute);
  } catch {
    // Private mode / storage disabled — the default #/edit bounce still applies.
  }
}

/**
 * Read WITHOUT consuming — for the failed-link page, where the user has not
 * signed in yet and the route they wanted must survive for the next attempt.
 */
export function peekReturnTo(): string | null {
  try {
    const value = window.localStorage.getItem(KEY);
    return value && isSafeHashRoute(value) ? value : null;
  } catch {
    return null;
  }
}

/** Read and clear the stored route. Returns null when there is nothing safe to use. */
export function takeReturnTo(): string | null {
  try {
    const value = window.localStorage.getItem(KEY);
    window.localStorage.removeItem(KEY);
    return value && isSafeHashRoute(value) ? value : null;
  } catch {
    return null;
  }
}
